/**
 * RadialNetwork — Acts 2 + 3 unified into one continuous reveal.
 *
 * The shield gallery from Act 1 is REPLACED by this radial map. Every
 * creator from the cohort is a node in the map, organized by role:
 *
 *   • Patient Zero — center, 1.4× scale, pink glow, gold crown
 *   • Bridges (3)   — inner ring, teal halos, thick lines to PZ
 *   • Amplifiers    — middle ring, full shields, medium lines to bridges
 *   • Long-tail     — outer ring, top 20 by unique reach, mini-shields
 *
 * Two layers of connections:
 *   1. STRUCTURAL — teal lines from PZ → bridges → amplifiers →
 *      long-tail. Shows the role hierarchy / firing path.
 *   2. OVERLAP    — TOP 8 amber arcs between non-adjacent creator pairs
 *      with the highest audience overlap. Thickness scales with
 *      strength so the brand sees rank, not noise.
 *
 * Stages (combined Acts 2 and 3, ~13.5s total):
 *   T+0.0s  materializing  — shields fade in at ring positions
 *   T+1.2s  edges_drawing  — structural lines + overlap arcs draw
 *   T+2.4s  checking       — 3 integrity checks tick with visual proof
 *   T+6.5s  centrality_scan — rolling teal sweep through rings (1.8s)
 *   T+8.3s  pz_reveal      — crown lands on Patient Zero
 *   T+9.5s  roles          — bridge→amplifier→long-tail color reveal
 *   T+10.7s axis           — time axis slides up, projection lines drop
 *   T+12.3s curve          — dashed teal compounding curve traces
 *   T+13.5s settled
 *
 * The curve is DASHED ONLY. Never filled. Never a solid area.
 * If it ever renders as a fill, it is a bug. There is no <path fill>
 * for the curve anywhere in this file.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Crown } from 'lucide-react';
import BriefingCreatorCard from './BriefingCreatorCard';
import MiniShield from './MiniShield';
import PulseMeter from './PulseMeter';

const TEAL   = '#00D4FF';
const PINK   = '#FF4D94';
const PURPLE = '#A78BFA';
const GRAY   = '#4A4A4A';
const GOLD   = '#FACC15';
const GREEN  = '#22D3A1';
const RED    = '#EF4444';
const AMBER  = '#F59E0B';

// Stage timing (cumulative ms)
const T_MATERIALIZE   = 0;
const T_EDGES         = 1200;
const T_CHECKS        = 2400;   // 3 checks × 1300ms = 3900ms
const T_SCAN_START    = 6500;
const T_PZ_REVEAL     = 8300;
const T_ROLES         = 9500;
const T_AXIS          = 10700;
const T_CURVE         = 12300;
const T_SETTLED       = 13500;
const CHECK_INTERVAL  = 1300;

// ─── Canvas layout ───────────────────────────────────────────────
// Canvas is split into two stacked zones:
//   • NETWORK_ZONE — top 420px, the radial map
//   • TIMELINE_ZONE — bottom 120px, the burst timeline (hero element)
// Total canvas height = 540px so the entire surface fits comfortably
// inside a 1440 × 900 viewport with the fixed narrator footer below.
const CANVAS_H   = 540;
const NETWORK_H  = 420;
const TIMELINE_H = 120;
const NETWORK_CENTER_Y_NORM = (NETWORK_H / 2) / CANVAS_H;

// Ring geometry — elliptical, sized so:
//   • Inner / middle rings sit well inside the network zone
//   • Outer ring's bottom edge stays ≥ 30px above the timeline zone
//   • Outer ring's left/right edges stay ≥ 5% inside the viewport so
//     mini-shields never clip at the canvas boundary
const RING = {
  inner:  { rx: 0.10, ry: 0.18 },    // bridges
  middle: { rx: 0.22, ry: 0.26 },    // amplifiers
  outer:  { rx: 0.36, ry: 0.32 },    // long-tail (single ring, top 20)
};

const SHIELD_SIZE = {
  patient_zero: 1.15,   // slightly larger than bridges
  bridge:       0.85,
  amplifier:    0.55,
  long_tail:    0.42,
};

const ROLE_COLOR = {
  patient_zero: PINK,
  bridge:       PURPLE,
  amplifier:    TEAL,
  long_tail:    GRAY,
};

// Visible long-tail cap — quality of the visible network over
// quantity of dots. The rest still exist in `roles` / `schedule` /
// projection-line targets, just don't render as nodes.
const MAX_LONG_TAIL_VISIBLE = 20;
// Visible overlap arcs cap — top 8 highest-overlap pairs only.
const MAX_OVERLAP_ARCS = 8;
// Amplifiers with full name labels (rest get hidden labels to
// prevent collisions on dense rings).
const NAMED_AMPLIFIERS = 5;

// ─── Polar to cartesian on an ellipse ───────────────────────────
function ellipseAt(cx, cy, rx, ry, angleRad) {
  return [cx + rx * Math.cos(angleRad), cy + ry * Math.sin(angleRad)];
}


export default function RadialNetwork({
  candidates,
  overlapEdges,        // [{from, to, strength}] from act2.edges
  integrity,
  patientZero,
  roles,
  schedule,
  predictedCurve,
  compoundingLabel,
  onCheckTick,
  onAct2Complete,
  onStageChange,
  onAct3Complete,
  isActive,
  // Act 4 props — owned by BriefingCanvas. The radial network is the
  // visual canvas; it renders ignition effects but doesn't decide WHEN
  // to fire.
  act4Phase = 'idle',  // 'idle' | 'countdown' | 'igniting' | 'handoff'
  pulseTarget = null,  // act4.projected_pulse_target (int | null)
  pulseCurrent = 0,    // live pre-polled pulse value (0..100)
}) {
  const [stage, setStage] = useState('materializing');
  const [activeCheckIdx, setActiveCheckIdx] = useState(-1);
  const [scanReachedRing, setScanReachedRing] = useState(-1);
  const [revealedRoles, setRevealedRoles] = useState(new Set());
  const [revealedBursts, setRevealedBursts] = useState(0);

  const firedCheckKeys = useRef(new Set());
  const onCheckTickRef = useRef(onCheckTick);
  const onAct2CompleteRef = useRef(onAct2Complete);
  const onStageRef = useRef(onStageChange);
  const onAct3CompleteRef = useRef(onAct3Complete);
  useEffect(() => { onCheckTickRef.current = onCheckTick; }, [onCheckTick]);
  useEffect(() => { onAct2CompleteRef.current = onAct2Complete; }, [onAct2Complete]);
  useEffect(() => { onStageRef.current = onStageChange; }, [onStageChange]);
  useEffect(() => { onAct3CompleteRef.current = onAct3Complete; }, [onAct3Complete]);

  // ─── Bucket creators by role ────────────────────────────────
  const byRole = useMemo(() => {
    const buckets = { patient_zero: [], bridge: [], amplifier: [], long_tail: [] };
    candidates.forEach(c => {
      const r = roles?.[c.handle] || 'long_tail';
      if (buckets[r]) buckets[r].push(c);
    });
    return buckets;
  }, [candidates, roles]);

  // ─── Top-N amplifiers by reach (for full-name labels) ──────
  const namedAmplifierSet = useMemo(() => {
    const sorted = [...byRole.amplifier].sort(
      (a, b) => (b.unique_reach_followers || b.followers || 0) -
                (a.unique_reach_followers || a.followers || 0),
    );
    return new Set(sorted.slice(0, NAMED_AMPLIFIERS).map(c => c.handle));
  }, [byRole.amplifier]);

  // ─── Compute ring positions for every visible creator ──────
  // Returns: Map<handle, {x, y, role, ringIdx, angle, size}>
  // Long-tail is CAPPED to top 20 by unique reach — the rest do not
  // render as visible nodes (no x/y entry in the map).
  const positions = useMemo(() => {
    const map = new Map();
    const cy = NETWORK_CENTER_Y_NORM;

    // PZ at network center
    const pz = byRole.patient_zero[0];
    if (pz) {
      map.set(pz.handle, {
        x: 0.5, y: cy,
        role: 'patient_zero',
        ringIdx: 0, angle: 0,
        size: SHIELD_SIZE.patient_zero,
      });
    }

    // Bridges — inner ring, equidistant. Start at top (-π/2).
    byRole.bridge.forEach((c, i) => {
      const n = byRole.bridge.length;
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      const [x, y] = ellipseAt(0.5, cy, RING.inner.rx, RING.inner.ry, angle);
      map.set(c.handle, {
        x, y, role: 'bridge', ringIdx: 1, angle,
        size: SHIELD_SIZE.bridge,
      });
    });

    // Amplifiers — middle ring, evenly distributed. Phase-offset by
    // π/n (half a step) so they sit BETWEEN bridge angles, not
    // directly above/below them — eliminates the vertical shield
    // collisions visible at the top/bottom of the prior layout.
    const MAX_FULL_AMPLIFIERS = 10;
    const fullAmps = byRole.amplifier.slice(0, MAX_FULL_AMPLIFIERS);
    const spillAmps = byRole.amplifier.slice(MAX_FULL_AMPLIFIERS);
    fullAmps.forEach((c, i) => {
      const n = fullAmps.length;
      const angle = -Math.PI / 2 + Math.PI / n + (i * 2 * Math.PI) / n;
      const [x, y] = ellipseAt(0.5, cy, RING.middle.rx, RING.middle.ry, angle);
      map.set(c.handle, {
        x, y, role: 'amplifier', ringIdx: 2, angle,
        size: SHIELD_SIZE.amplifier,
      });
    });

    // Long-tail — SINGLE outer ring, capped at top 20 by unique reach.
    // Spill amplifiers join the pool. The rest exist in network data
    // but don't render as nodes.
    const candidateTail = [...spillAmps, ...byRole.long_tail];
    candidateTail.sort(
      (a, b) => (b.unique_reach_followers || b.followers || 0) -
                (a.unique_reach_followers || a.followers || 0),
    );
    const visibleTail = candidateTail.slice(0, MAX_LONG_TAIL_VISIBLE);
    visibleTail.forEach((c, i) => {
      const n = visibleTail.length;
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      const [x, y] = ellipseAt(0.5, cy, RING.outer.rx, RING.outer.ry, angle);
      const actualRole = roles?.[c.handle] || 'long_tail';
      map.set(c.handle, {
        x, y, role: actualRole,
        ringIdx: 3, angle,
        size: SHIELD_SIZE.long_tail,
      });
    });

    return map;
  }, [byRole, roles]);

  // ─── Structural edges ─────────────────────────────────────
  // PZ → each bridge. Bridges → nearest amplifiers. Amplifiers →
  // nearest long-tails — only among VISIBLE positions.
  const structuralEdges = useMemo(() => {
    const out = [];
    const pz = byRole.patient_zero[0];
    if (!pz) return out;
    // PZ → bridges (always)
    byRole.bridge.forEach(b => {
      const overlap = (overlapEdges || []).find(e =>
        (e.from === pz.handle && e.to === b.handle) ||
        (e.to === pz.handle && e.from === b.handle)
      );
      out.push({
        from: pz.handle,
        to: b.handle,
        weight: overlap ? overlap.strength : 0.4,
        kind: 'pz_bridge',
      });
    });
    // Each bridge → nearest 4 amplifiers (by ring angle proximity)
    byRole.bridge.forEach(b => {
      const bPos = positions.get(b.handle);
      if (!bPos) return;
      const amps = [];
      byRole.amplifier.forEach(a => {
        const aPos = positions.get(a.handle);
        if (!aPos) return;
        const da = Math.min(
          Math.abs(aPos.angle - bPos.angle),
          2 * Math.PI - Math.abs(aPos.angle - bPos.angle),
        );
        amps.push({ amp: a, da });
      });
      amps.sort((p, q) => p.da - q.da);
      amps.slice(0, 4).forEach(({ amp }) => {
        out.push({
          from: b.handle,
          to: amp.handle,
          weight: 0.3,
          kind: 'bridge_amp',
        });
      });
    });
    // Each amplifier → nearest 2 long-tail-VISIBLE (positions exist)
    byRole.amplifier.forEach(a => {
      const aPos = positions.get(a.handle);
      if (!aPos) return;
      const tails = [];
      byRole.long_tail.forEach(t => {
        const tPos = positions.get(t.handle);
        if (!tPos) return;   // skip invisible long-tails
        const dist = Math.hypot(tPos.x - aPos.x, tPos.y - aPos.y);
        tails.push({ tail: t, dist });
      });
      tails.sort((p, q) => p.dist - q.dist);
      tails.slice(0, 2).forEach(({ tail }) => {
        out.push({
          from: a.handle,
          to: tail.handle,
          weight: 0.18,
          kind: 'amp_tail',
        });
      });
    });

    // Coverage pass — every VISIBLE long-tail must connect to the
    // constellation. Without this, outer-ring shields at extreme
    // angles can appear "detached" because no amplifier happens to
    // have them in its nearest-2. Iterate visible long-tails and add
    // an edge to their nearest amplifier if no edge already exists.
    const existing = new Set(out.map(e => [e.from, e.to].sort().join('::')));
    byRole.long_tail.forEach(t => {
      const tPos = positions.get(t.handle);
      if (!tPos) return;
      let nearest = null;
      let bestDist = Infinity;
      byRole.amplifier.forEach(a => {
        const aPos = positions.get(a.handle);
        if (!aPos) return;
        const d = Math.hypot(tPos.x - aPos.x, tPos.y - aPos.y);
        if (d < bestDist) { bestDist = d; nearest = a; }
      });
      if (!nearest) return;
      const key = [nearest.handle, t.handle].sort().join('::');
      if (!existing.has(key)) {
        existing.add(key);
        out.push({
          from: nearest.handle,
          to: t.handle,
          weight: 0.14,
          kind: 'amp_tail',
        });
      }
    });
    return out;
  }, [byRole, positions, overlapEdges]);

  // ─── Overlap arcs ────────────────────────────────────────
  // TOP 8 amber arcs by strength — sorted descending, then sliced.
  // Excludes pairs already rendered as structural edges and any pair
  // whose endpoints aren't both visible.
  const overlapArcs = useMemo(() => {
    const structuralSet = new Set(
      structuralEdges.map(e => [e.from, e.to].sort().join('::')),
    );
    const arcs = (overlapEdges || [])
      .filter(e => !structuralSet.has([e.from, e.to].sort().join('::')))
      .filter(e => positions.has(e.from) && positions.has(e.to))
      .slice()
      .sort((a, b) => (b.strength || 0) - (a.strength || 0))
      .slice(0, MAX_OVERLAP_ARCS);
    return arcs;
  }, [overlapEdges, structuralEdges, positions]);

  // ─── Handle → burst index map (for projection lines) ─────
  // Each visible creator projects DOWN to the burst they're scheduled
  // in. Default to burst 1 if not found in the schedule.
  const handleToBurst = useMemo(() => {
    const map = new Map();
    (schedule || []).forEach(b => {
      (b.handles || []).forEach(h => map.set(h, b));
    });
    return map;
  }, [schedule]);

  // ─── Run the unified timeline ─────────────────────────────
  useEffect(() => {
    if (!isActive) return undefined;
    setStage('materializing');
    setActiveCheckIdx(-1);
    setScanReachedRing(-1);
    setRevealedRoles(new Set());
    setRevealedBursts(0);
    firedCheckKeys.current = new Set();

    const timers = [];

    // Edges
    timers.push(setTimeout(() => {
      setStage('edges_drawing');
      onStageRef.current?.('edges_drawing');
    }, T_EDGES));

    // Checks
    timers.push(setTimeout(() => {
      setStage('checking');
      onStageRef.current?.('checking');
    }, T_CHECKS));

    (integrity?.checks || []).forEach((c, idx) => {
      const at = T_CHECKS + idx * CHECK_INTERVAL;
      timers.push(setTimeout(() => {
        setActiveCheckIdx(idx);
        if (!firedCheckKeys.current.has(c.key)) {
          firedCheckKeys.current.add(c.key);
          onCheckTickRef.current?.({ key: c.key, passed: c.passed });
        }
      }, at));
    });

    // Act 2 wrap-up
    const checksEnd = T_CHECKS + (integrity?.checks?.length || 3) * CHECK_INTERVAL;
    timers.push(setTimeout(() => {
      const firstFailing = (integrity?.checks || []).find(c => !c.passed) || null;
      onAct2CompleteRef.current?.(firstFailing);
    }, checksEnd + 100));

    // Centrality scan (Act 3 begins)
    timers.push(setTimeout(() => {
      setStage('centrality_scan');
      onStageRef.current?.('centrality_scan');
    }, T_SCAN_START));
    [0, 1, 2, 3].forEach((r, i) => {
      timers.push(setTimeout(
        () => setScanReachedRing(r),
        T_SCAN_START + i * 400,
      ));
    });

    // PZ reveal
    timers.push(setTimeout(() => {
      setStage('pz_reveal');
      onStageRef.current?.('pz_reveal');
    }, T_PZ_REVEAL));

    // Roles
    timers.push(setTimeout(() => {
      setStage('roles');
      onStageRef.current?.('roles');
    }, T_ROLES));
    const roleOrder = ['bridge', 'amplifier', 'long_tail'];
    roleOrder.forEach((r, idx) => {
      timers.push(setTimeout(() => {
        setRevealedRoles(prev => new Set(prev).add(r));
      }, T_ROLES + idx * 400));
    });

    // Time axis + bursts
    timers.push(setTimeout(() => {
      setStage('axis');
      onStageRef.current?.('axis');
    }, T_AXIS));
    for (let b = 1; b <= 4; b++) {
      timers.push(setTimeout(
        () => setRevealedBursts(b),
        T_AXIS + b * 280,
      ));
    }

    // Curve
    timers.push(setTimeout(() => {
      setStage('curve');
      onStageRef.current?.('curve');
    }, T_CURVE));

    // Settled
    timers.push(setTimeout(() => {
      setStage('settled');
      onStageRef.current?.('settled');
      onAct3CompleteRef.current?.();
    }, T_SETTLED));

    return () => timers.forEach(clearTimeout);
  }, [isActive, integrity]);

  // ─── Layout: which roles have "revealed" their color yet ─
  const isPostRoles = ['roles', 'axis', 'curve', 'settled'].includes(stage);
  const isPostCrown = ['pz_reveal', 'roles', 'axis', 'curve', 'settled'].includes(stage);
  const isPostAxis  = ['axis', 'curve', 'settled'].includes(stage);
  const isPostCurve = ['curve', 'settled'].includes(stage);

  // ─── Active check effects (during 'checking' stage) ─────
  const activeCheck = integrity?.checks?.[activeCheckIdx];
  const cascadePairHandles = useMemo(() => {
    if (activeCheck?.key !== 'cascade_seed') return new Set();
    const set = new Set();
    (activeCheck.constellation?.pairs || []).forEach(p => {
      set.add(p[0]); set.add(p[1]);
    });
    return set;
  }, [activeCheck]);
  const cascadeEdges = useMemo(() => {
    if (activeCheck?.key !== 'cascade_seed') return [];
    return activeCheck.constellation?.pairs || [];
  }, [activeCheck]);
  const bridgeHaloHandles = useMemo(() => {
    if (activeCheck?.key !== 'bridges') return new Set();
    return new Set(activeCheck.constellation?.handles || []);
  }, [activeCheck]);
  const expansionRatio = activeCheck?.key === 'expansion_lift'
    ? (activeCheck.constellation?.ratio ?? 0)
    : 0;

  const pzPos = patientZero ? positions.get(patientZero.handle) : null;

  // ─── Determine nameMode for each visible card ─────────────
  function nameModeForHandle(handle) {
    const pos = positions.get(handle);
    if (!pos) return 'hidden';
    if (pos.role === 'patient_zero') return 'full';
    if (pos.role === 'bridge')       return 'full';
    if (pos.role === 'amplifier') {
      return namedAmplifierSet.has(handle) ? 'name-only' : 'hidden';
    }
    return 'hidden';
  }

  // ─── Timeline coordinate helpers (used by projection lines + axis) ─
  // ALL of these are in the SVG's viewBox coords (0..1000 × 0..CANVAS_H).
  const TRACK_Y       = CANVAS_H - 70;        // 550
  const PAD_X         = 80;
  const PLOT_W        = 1000 - PAD_X * 2;     // 840
  const burstXForT    = (t) => PAD_X + (t / 72) * PLOT_W;
  // Compounding curve floats above the track by 20px (in SVG coords).
  const CURVE_TOP_Y    = TRACK_Y - 90;
  const CURVE_BOTTOM_Y = TRACK_Y - 20;
  const vMax = Math.max(...(predictedCurve || []).map(p => p.velocity), 1);
  const curveD = (predictedCurve || []).map((p, i) => {
    const x = burstXForT(p.t);
    const y = CURVE_BOTTOM_Y - (p.velocity / vMax) * (CURVE_BOTTOM_Y - CURVE_TOP_Y);
    return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
  }).join(' ');

  // Build projection-line targets — only for visible nodes.
  const projectionLines = useMemo(() => {
    if (!isPostAxis) return [];
    const out = [];
    positions.forEach((pos, handle) => {
      const burst = handleToBurst.get(handle);
      if (!burst) return;
      if (burst.burst > revealedBursts) return;   // line lights up after burst reveal
      out.push({
        handle,
        role: pos.role,
        x1: pos.x * 1000,
        y1: pos.y * CANVAS_H,
        x2: burstXForT(burst.t_hours),
        y2: TRACK_Y,
        burstIdx: burst.burst,
      });
    });
    return out;
  }, [positions, handleToBurst, isPostAxis, revealedBursts]);

  // ─── Act 4 ignition derived state ───────────────────────────
  const isIgniting = act4Phase === 'igniting' || act4Phase === 'handoff';
  // First segment of the curve (T+0 → ~T+12h) turns SOLID PINK when
  // ignition fires — "the wave has begun." The rest stays dashed teal.
  const ignitedSegmentD = useMemo(() => {
    if (!predictedCurve || predictedCurve.length < 2) return '';
    const pts = predictedCurve.slice(0, 4);
    return pts.map((p, i) => {
      const x = burstXForT(p.t);
      const y = CURVE_BOTTOM_Y - (p.velocity / vMax) * (CURVE_BOTTOM_Y - CURVE_TOP_Y);
      return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
    }).join(' ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predictedCurve, vMax]);

  return (
    <div
      data-testid="briefing-radial-network"
      data-stage={stage}
      className="relative w-full overflow-hidden"
      style={{ height: CANVAS_H }}
    >
      {/* Background SVG — structural lines + overlap arcs + scan sweep +
          projection lines + timeline + dashed curve. All in one coord
          space so the projection lines naturally land on the timeline
          burst markers. */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox={`0 0 1000 ${CANVAS_H}`}
        preserveAspectRatio="xMidYMid meet"
        data-testid="radial-edges-svg"
      >
        {/* Faint outer-ring guide — makes the constellation geometry
            visible even where individual shields don't have strong
            structural lines. Long-tail nodes that sit on the ring read
            as PART of the ring, not as floating elements. */}
        {(stage !== 'materializing') && (
          <ellipse
            data-testid="outer-ring-guide"
            cx={500}
            cy={NETWORK_CENTER_Y_NORM * CANVAS_H}
            rx={RING.outer.rx * 1000}
            ry={RING.outer.ry * CANVAS_H}
            fill="none"
            stroke={TEAL}
            strokeOpacity={0.14}
            strokeWidth={1}
            strokeDasharray="2 4"
            style={{ animation: 'radialArcFade 800ms ease-out both' }}
          />
        )}

        {/* Structural edges */}
        {(stage !== 'materializing') && structuralEdges.map((e, i) => {
          const from = positions.get(e.from);
          const to = positions.get(e.to);
          if (!from || !to) return null;
          const x1 = from.x * 1000, y1 = from.y * CANVAS_H;
          const x2 = to.x * 1000,   y2 = to.y * CANVAS_H;
          const isCascadeHit = cascadeEdges.some(
            ([a, b]) => (a === e.from && b === e.to) || (a === e.to && b === e.from)
          );
          // During ignition every structural edge recolors PINK with a
          // stronger weight + drop-shadow — but SEQUENTIALLY by role so
          // the cascade reads as a wave propagating OUT from Patient
          // Zero, not a single frame of everything turning pink at
          // once. Per the spec: PZ→Bridge edges fire first (0ms), then
          // Bridge→Amp edges (400ms), then Amp→Tail edges (800ms).
          const igniteThis = isIgniting;
          const ignitionDelayMs =
            e.kind === 'pz_bridge'  ? 0   :
            e.kind === 'bridge_amp' ? 400 :
            /* amp_tail */            800;
          const stroke = igniteThis ? PINK : (isCascadeHit ? PINK : TEAL);
          const opacity = igniteThis
            ? Math.min(0.95, 0.5 + e.weight * 1.2)
            : (isCascadeHit ? 0.85 : Math.min(0.6, e.weight * 1.4));
          const width = igniteThis
            ? Math.max(1.4, e.weight * 4.5)
            : (isCascadeHit ? 2.6 : Math.max(0.6, e.weight * 3.5));
          return (
            <line
              key={`s-${e.from}-${e.to}`}
              data-testid={`radial-edge-${i}`}
              data-edge-kind={e.kind}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={stroke}
              strokeOpacity={opacity}
              strokeWidth={width}
              style={{
                // Each property transitions with its OWN delay scoped to
                // this edge's kind, so the recolor wave rolls outward
                // from PZ rather than firing simultaneously.
                transition: igniteThis
                  ? `stroke 500ms ease-out ${ignitionDelayMs}ms, stroke-opacity 500ms ease-out ${ignitionDelayMs}ms, stroke-width 500ms ease-out ${ignitionDelayMs}ms, filter 500ms ease-out ${ignitionDelayMs}ms`
                  : 'stroke 250ms ease-out, stroke-opacity 250ms ease-out, stroke-width 250ms ease-out',
                animation:  'radialEdgeDraw 600ms ease-out both',
                animationDelay: `${e.kind === 'pz_bridge' ? 0 : e.kind === 'bridge_amp' ? 200 : 400}ms`,
                filter: igniteThis ? `drop-shadow(0 0 6px ${PINK}aa)` : 'none',
              }}
            />
          );
        })}

        {/* Overlap arcs — TOP 8 by strength. Thickness scales with
            strength so the strongest pair is the visually thickest. */}
        {(stage !== 'materializing') && overlapArcs.map((arc, i) => {
          const from = positions.get(arc.from);
          const to = positions.get(arc.to);
          if (!from || !to) return null;
          const x1 = from.x * 1000, y1 = from.y * CANVAS_H;
          const x2 = to.x * 1000,   y2 = to.y * CANVAS_H;
          // Quadratic curve toward network center
          const ncy = NETWORK_CENTER_Y_NORM * CANVAS_H;
          const cx = (x1 + x2) / 2 + (500 - (x1 + x2) / 2) * 0.3;
          const cy = (y1 + y2) / 2 + (ncy - (y1 + y2) / 2) * 0.3;
          const d = `M ${x1},${y1} Q ${cx},${cy} ${x2},${y2}`;
          // Strength-weighted: top arc ~4.2px, weakest ~1.4px
          const sw = 1.4 + (arc.strength || 0) * 3.2;
          const op = 0.45 + (arc.strength || 0) * 0.35;
          return (
            <path
              key={`a-${arc.from}-${arc.to}`}
              data-testid={`overlap-arc-${i}`}
              d={d}
              fill="none"
              stroke={AMBER}
              strokeOpacity={Math.min(0.88, op)}
              strokeWidth={sw}
              strokeLinecap="round"
              style={{
                animation: 'radialArcFade 800ms ease-out both',
                animationDelay: `${300 + i * 60}ms`,
              }}
            />
          );
        })}

        {/* Centrality scan ring */}
        {stage === 'centrality_scan' && pzPos && (
          <circle
            cx={pzPos.x * 1000} cy={pzPos.y * CANVAS_H}
            r={(scanReachedRing + 1) * 80}
            fill="none"
            stroke={TEAL}
            strokeOpacity={0.4}
            strokeWidth={2}
            strokeDasharray="6 4"
            style={{ transition: 'r 380ms ease-out' }}
            data-testid="centrality-scan-ring"
          />
        )}

        {/* Expansion lift halo */}
        {activeCheck?.key === 'expansion_lift' && expansionRatio > 0 && (
          <circle
            cx={500} cy={NETWORK_CENTER_Y_NORM * CANVAS_H}
            r={140 + expansionRatio * 280}
            fill="none"
            stroke={GREEN}
            strokeWidth={2}
            strokeDasharray="6 4"
            opacity={0.7}
            data-testid="expansion-halo"
            style={{ animation: 'radialExpansionFade 1.2s ease-out both' }}
          />
        )}

        {/* Ignition shockwave — pink ring radiating out from PZ at the
            moment the wave fires. Light propagates along the structural
            edges via the cascade animation layered below. */}
        {isIgniting && pzPos && (
          <>
            <circle
              data-testid="ignition-shockwave-1"
              cx={pzPos.x * 1000} cy={pzPos.y * CANVAS_H}
              fill="none"
              stroke={PINK}
              strokeWidth={3}
              style={{
                animation: 'ignitionShockwave 1600ms ease-out both',
                transformOrigin: `${pzPos.x * 1000}px ${pzPos.y * CANVAS_H}px`,
              }}
            />
            <circle
              data-testid="ignition-shockwave-2"
              cx={pzPos.x * 1000} cy={pzPos.y * CANVAS_H}
              fill="none"
              stroke={PINK}
              strokeWidth={2}
              opacity={0.6}
              style={{
                animation: 'ignitionShockwave 2000ms ease-out 220ms both',
              }}
            />
          </>
        )}

        {/* ─── TIMELINE ─────────────────────────────────────
            Track is a SOLID bright line — the floor. The dashed
            compounding curve sits above the track as a separate layer.
            Projection lines drop from shield positions down to their
            burst markers on the track. */}
        {isPostAxis && (
          <g data-testid="timeline-zone" style={{ animation: 'radialAxisRise 600ms ease-out both' }}>
            {/* Projection lines — thin role-colored guides from shield
                to burst marker. Animated in once a burst is revealed. */}
            {projectionLines.map((pl) => (
              <line
                key={`proj-${pl.handle}`}
                data-testid={`projection-line-${pl.handle}`}
                x1={pl.x1} y1={pl.y1}
                x2={pl.x2} y2={pl.y2}
                stroke={ROLE_COLOR[pl.role] || GRAY}
                strokeOpacity={pl.role === 'patient_zero' ? 0.55
                              : pl.role === 'bridge'      ? 0.32
                              : pl.role === 'amplifier'   ? 0.22
                              : 0.12}
                strokeWidth={pl.role === 'patient_zero' ? 1.3 : 0.8}
                strokeDasharray={pl.role === 'patient_zero' ? '' : '2 3'}
                style={{
                  animation: 'radialProjectionFade 500ms ease-out both',
                  animationDelay: `${pl.burstIdx * 60}ms`,
                }}
              />
            ))}

            {/* TRACK — solid bright line. NOT dashed. The floor of
                the timeline. */}
            <line
              x1={PAD_X} y1={TRACK_Y}
              x2={1000 - PAD_X} y2={TRACK_Y}
              stroke="rgba(255,255,255,0.55)"
              strokeWidth={1.8}
              data-testid="timeline-track"
            />

            {/* Burst markers — vertical tick (16px), circle node on
                track, and time label below. */}
            {(schedule || []).map(b => {
              const x = burstXForT(b.t_hours);
              const lit = b.burst <= revealedBursts;
              const isFire = b.burst === 1;
              const tickColor = lit
                ? (isFire ? PINK : TEAL)
                : 'rgba(255,255,255,0.25)';
              return (
                <g key={b.burst} data-testid={`burst-marker-${b.burst}`}>
                  {/* 16px vertical tick spanning the track */}
                  <line
                    x1={x} y1={TRACK_Y - 8}
                    x2={x} y2={TRACK_Y + 8}
                    stroke={tickColor}
                    strokeWidth={lit ? 2.2 : 1.4}
                  />
                  {/* Circle node on the track */}
                  <circle
                    cx={x} cy={TRACK_Y}
                    r={lit ? 5.5 : 3.5}
                    fill={tickColor}
                    stroke={lit ? '#0A0A0A' : 'transparent'}
                    strokeWidth={lit ? 1 : 0}
                    style={{
                      filter: lit ? `drop-shadow(0 0 6px ${tickColor})` : 'none',
                    }}
                  />
                </g>
              );
            })}

            {/* DASHED COMPOUNDING CURVE — floats above the track as a
                separate layer. The track is the floor; this is the
                forecast that compounds above it. fill=none, never
                a filled area. Never a <path fill>.
                Stroke weight + opacity tuned so the curve reads from
                across the room — the visual payoff of Act 3. */}
            {isPostCurve && (
              <path
                d={curveD}
                fill="none"
                stroke={TEAL}
                strokeWidth={2.8}
                strokeDasharray="6 4"
                strokeLinecap="round"
                opacity={0.95}
                style={{
                  animation: 'radialCurveFade 900ms ease-out both',
                  filter:    `drop-shadow(0 0 10px ${TEAL}cc)`,
                }}
                data-testid="predicted-curve"
              />
            )}

            {/* Ignited first segment — SOLID PINK overlay covering
                T+0 → ~T+12h once ignition fires. Sits on top of the
                dashed teal curve. The dashed teal still exists beneath
                (it's the forecast); the pink solid says "this part is
                no longer prediction — it's happening." */}
            {isIgniting && (
              <path
                data-testid="ignited-segment"
                d={ignitedSegmentD}
                fill="none"
                stroke={PINK}
                strokeWidth={3.4}
                strokeLinecap="round"
                opacity={1}
                style={{
                  animation: 'ignitedSegmentDraw 1200ms ease-out both',
                  filter:    `drop-shadow(0 0 14px ${PINK})`,
                  strokeDasharray: 1200,
                }}
              />
            )}
          </g>
        )}
      </svg>

      {/* Shield layer — absolute positioned at ring coordinates */}
      {candidates.map(c => {
        const pos = positions.get(c.handle);
        if (!pos) return null;
        const isPZ = pos.role === 'patient_zero';
        const isFull = pos.size >= SHIELD_SIZE.amplifier;
        const isCascadeFlash = cascadePairHandles.has(c.handle);
        const isBridgeHalo = bridgeHaloHandles.has(c.handle);
        const isVisibleRoleColor = isPZ || isPostRoles || revealedRoles.has(pos.role);

        const wPx = 134 * pos.size;
        const hPx = 170 * pos.size;

        return (
          <div
            key={c.handle}
            data-testid={`radial-shield-${c.handle}`}
            data-role={pos.role}
            data-revealed={isVisibleRoleColor ? 'true' : 'false'}
            className="absolute"
            style={{
              left:      `${pos.x * 100}%`,
              top:       `${pos.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              width:     isFull ? wPx : 48,
              height:    isFull ? hPx : 48,
              zIndex:    isPZ ? 30 : isFull ? 20 : 10,
              animation: 'radialShieldRise 600ms ease-out both',
              animationDelay: isPZ ? '0ms'
                            : pos.role === 'bridge'    ? '200ms'
                            : pos.role === 'amplifier' ? '400ms'
                            : '600ms',
              filter: isPZ && isIgniting
                ? `drop-shadow(0 0 56px ${PINK}) drop-shadow(0 0 96px ${PINK}cc)`
                : isPZ && isPostCrown
                  ? `drop-shadow(0 0 24px ${PINK}aa)`
                  : isCascadeFlash
                    ? `drop-shadow(0 0 14px ${PINK}cc)`
                    : isBridgeHalo
                      ? `drop-shadow(0 0 12px ${TEAL}aa)`
                      : 'none',
              transition: 'filter 400ms ease-out',
            }}
          >
            {isFull ? (
              <BriefingCreatorCard
                creator={c}
                nameMode={nameModeForHandle(c.handle)}
              />
            ) : (
              <MiniShield
                creator={c}
                role={isVisibleRoleColor ? pos.role : 'long_tail'}
                size={48 * pos.size / 0.42}
                dim={!isPostRoles && pos.role !== 'patient_zero' && !revealedRoles.has(pos.role)}
              />
            )}
          </div>
        );
      })}

      {/* Patient Zero crown — sits a clear 16px above the top edge of
          the PZ shield. PZ shield is 195.5px tall (170×1.15), so its
          top edge is 97.75px above shield center. The crown div is
          ~45px tall and is positioned by `top: pos.y%` (anchored at
          shield CENTER), then translate(-50%, calc(-50% - Xpx)) moves
          it up. div_bottom = shield_center - X + 22.5. To put
          div_bottom 16px above shield_top: X = 97.75 + 22.5 + 16 ≈ 138. */}
      {pzPos && isPostCrown && (
        <div
          data-testid="pz-crown"
          className="absolute pointer-events-none"
          style={{
            left:      `${pzPos.x * 100}%`,
            top:       `${pzPos.y * 100}%`,
            transform: `translate(-50%, calc(-50% - 138px))`,
            zIndex:    40,
            animation: 'radialCrownLand 600ms ease-out both',
          }}
        >
          <div
            className="px-2.5 py-1 rounded-full flex items-center gap-1.5"
            style={{
              background: `linear-gradient(180deg, ${GOLD}, ${PINK})`,
              boxShadow:  `0 0 18px ${PINK}aa`,
            }}
          >
            <Crown className="h-3.5 w-3.5 text-black" strokeWidth={2.5} />
            <span
              className="text-[9px] font-black uppercase tracking-[0.18em] text-black"
              data-testid="pz-name"
            >
              Patient Zero
            </span>
          </div>
          <p
            className="text-[10px] text-center mt-1.5 font-black"
            style={{
              color: '#FFF',
              textShadow: `0 1px 4px rgba(0,0,0,0.95), 0 0 6px ${PINK}`,
              letterSpacing: '0.04em',
            }}
          >
            centrality {patientZero?.centrality_score}
          </p>
        </div>
      )}

      {/* Integrity check banner — sits ABOVE the network in the
          dedicated 32px clear band at the top of the canvas. Stays
          out of node label airspace, full-legibility size. */}
      {stage === 'checking' && activeCheck && (
        <div
          data-testid={`check-banner-${activeCheck.key}`}
          data-passed={activeCheck.passed ? 'true' : 'false'}
          className="absolute top-3 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-lg pointer-events-auto"
          style={{
            background: 'rgba(15,15,15,0.95)',
            border:     `1.5px solid ${activeCheck.passed ? GREEN : RED}`,
            boxShadow:  `0 0 28px ${activeCheck.passed ? GREEN : RED}66`,
            backdropFilter: 'blur(8px)',
            animation:  'radialCheckRise 280ms ease-out both',
            zIndex:     50,
            maxWidth:   '92%',
          }}
        >
          <div className="flex items-center gap-2.5 flex-wrap justify-center">
            <span
              className="text-[11px] uppercase tracking-[0.18em] font-black"
              style={{ color: activeCheck.passed ? GREEN : RED }}
            >
              {activeCheck.passed ? '✓ Passed' : '✗ Weak'}
            </span>
            <span className="text-[11px] text-gray-600">·</span>
            <span className="text-[12.5px] font-bold text-white">
              {activeCheck.label}
            </span>
            <span className="text-[11px] text-gray-600">·</span>
            <span className="text-[11.5px] text-gray-200">
              {activeCheck.detail}
            </span>
            {activeCheck.benchmark && (
              <>
                <span className="text-[11px] text-gray-700">·</span>
                <span
                  className="text-[11px] font-bold text-gray-400"
                  data-testid={`check-benchmark-${activeCheck.key}`}
                >
                  benchmark {activeCheck.benchmark}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Phase labels + time labels — rendered as HTML overlays so they
          can use the platform's font system at full legibility. SVG
          text would not antialias cleanly at small sizes. */}
      {isPostAxis && (
        <div
          data-testid="timeline-labels"
          className="absolute inset-0 pointer-events-none"
          style={{ animation: 'radialAxisRise 600ms ease-out both' }}
        >
          {(schedule || []).map((b, idx, arr) => {
            const xPct = (burstXForT(b.t_hours) / 1000) * 100;
            const lit = b.burst <= revealedBursts;
            const isFire = b.burst === 1;
            const phaseColor = isFire ? PINK
                             : b.burst === 4 ? GRAY
                             : TEAL;
            const phaseTopPct = ((TRACK_Y - 28) / CANVAS_H) * 100;
            const timeTopPct  = ((TRACK_Y + 16) / CANVAS_H) * 100;
            // Anchor first burst to its left edge and last burst to its
            // right edge so long labels (SATURATION, PATIENT ZERO FIRES)
            // never extend past the canvas viewport at any width.
            const isFirst = idx === 0;
            const isLast  = idx === arr.length - 1;
            const labelTransform = isFirst ? 'translateX(0)'
                                 : isLast  ? 'translateX(-100%)'
                                 : 'translateX(-50%)';
            return (
              <React.Fragment key={`labels-${b.burst}`}>
                {/* PHASE label above the track (13px, role-colored) */}
                <div
                  data-testid={`phase-label-${b.burst}`}
                  className="absolute"
                  style={{
                    left: `${xPct}%`,
                    top:  `${phaseTopPct}%`,
                    transform: labelTransform,
                    color: lit ? phaseColor : 'rgba(255,255,255,0.25)',
                    fontSize: 13,
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    textShadow: lit ? `0 0 14px ${phaseColor}55` : 'none',
                    transition: 'color 280ms ease-out',
                  }}
                >
                  {b.label}
                </div>
                {/* TIME label below the track (16px, white when lit) */}
                <div
                  data-testid={`time-label-${b.burst}`}
                  className="absolute font-black"
                  style={{
                    left: `${xPct}%`,
                    top:  `${timeTopPct}%`,
                    transform: labelTransform,
                    color: lit ? '#FFF' : 'rgba(255,255,255,0.35)',
                    fontSize: 16,
                    letterSpacing: '0.04em',
                    transition: 'color 280ms ease-out',
                  }}
                >
                  T+{b.t_hours}h
                </div>
              </React.Fragment>
            );
          })}

          {/* Compounding label — the Act 3 payoff line. Positioned at
              top-LEFT of the timeline zone so it never collides with
              (a) the Fire button anchored to the bottom-right of the
              viewport during the settled state, or (b) the PulseMeter
              that materializes top-right of the canvas during ignition.
              Left-anchored with explicit max-width so the full
              "Predicted to compound 1.3× faster than organic" text
              renders at every viewport width without truncation. */}
          {isPostCurve && compoundingLabel && (
            <div
              data-testid="compounding-label"
              className="absolute px-3 py-1.5 rounded-md"
              style={{
                top: `${((CURVE_TOP_Y - 30) / CANVAS_H) * 100}%`,
                left: 24,
                maxWidth: 'calc(50% - 32px)',
                background: 'rgba(0,212,255,0.10)',
                border:     `1px solid ${TEAL}66`,
                backdropFilter: 'blur(6px)',
              }}
            >
              <p
                className="text-[12px] font-bold leading-tight"
                style={{ color: TEAL, whiteSpace: 'nowrap', overflow: 'visible' }}
              >
                {compoundingLabel}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pulse Meter — born at 0 when ignition fires. Sits top-right
          of the canvas in the same column as the compounding label.
          Ghost target pink-tick + label only renders when backend
          provides projected_pulse_target (safe-surface contract). */}
      {isIgniting && (
        <div
          data-testid="pulse-meter-mount"
          className="absolute"
          style={{
            top: 16,
            right: 16,
            zIndex: 45,
          }}
        >
          <PulseMeter target={pulseTarget} current={pulseCurrent} visible={isIgniting} />
        </div>
      )}

      {/* Scoped keyframes */}
      <style>{`
        @keyframes radialShieldRise {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1);   }
        }
        @keyframes radialEdgeDraw {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes radialArcFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes radialExpansionFade {
          from { opacity: 0; }
          to   { opacity: 0.7; }
        }
        @keyframes radialCrownLand {
          from { opacity: 0; transform: translate(-50%, -180%) scale(0.4); }
          to   { opacity: 1; transform: translate(-50%, calc(-50% - 138px)) scale(1); }
        }
        @keyframes radialCheckRise {
          from { opacity: 0; transform: translate(-50%, -8px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes radialAxisRise {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes radialCurveFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes radialProjectionFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes ignitionShockwave {
          0%   { r: 24;  opacity: 0.9; }
          60%  { r: 320; opacity: 0.4; }
          100% { r: 520; opacity: 0;   }
        }
        @keyframes ignitedSegmentDraw {
          from { stroke-dashoffset: 1200; opacity: 0; }
          to   { stroke-dashoffset: 0;    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
